import React, { useState, useEffect } from 'react';
import { FiX, FiSave } from 'react-icons/fi';
import { motion } from 'framer-motion';
import api from '../../../api';

const LessonPlanModal = ({ isOpen, onClose, onSuccess, classItem, subject, labBatch, facultyId, editingPlan }) => {
  const [formData, setFormData] = useState({
    lessonNumber: '',
    plannedDate: '',
    topic: '',
    subtopics: '',
    learningObjectives: '',
    teachingMethodology: 'lecture',
    resourcesUsed: '',
    additionalNotes: '',
    isExtraClass: false,
    status: 'planned',
    actualDate: '',
    completionPercentage: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const methodologies = [
    'lecture', 'discussion', 'demo', 'lab', 'workshop', 'seminar',
    'presentation', 'group_activity', 'case_study', 'field_trip',
    'online', 'hybrid', 'other'
  ];

  useEffect(() => {
    if (editingPlan) {
      setFormData({
        lessonNumber: editingPlan.lessonNumber || '',
        plannedDate: editingPlan.plannedDate ? new Date(editingPlan.plannedDate).toISOString().split('T')[0] : '',
        topic: editingPlan.topic || '',
        subtopics: editingPlan.subtopics?.join(', ') || '',
        learningObjectives: editingPlan.learningObjectives?.join(', ') || '',
        teachingMethodology: editingPlan.teachingMethodology || 'lecture',
        resourcesUsed: editingPlan.resourcesUsed?.join(', ') || '',
        additionalNotes: editingPlan.additionalNotes || '',
        isExtraClass: editingPlan.isExtraClass || false,
        status: editingPlan.status || 'planned',
        actualDate: editingPlan.actualDate ? new Date(editingPlan.actualDate).toISOString().split('T')[0] : '',
        completionPercentage: editingPlan.completionPercentage || 0
      });
    } else {
      setFormData({
        lessonNumber: '',
        plannedDate: '',
        topic: '',
        subtopics: '',
        learningObjectives: '',
        teachingMethodology: 'lecture',
        resourcesUsed: '',
        additionalNotes: '',
        isExtraClass: false,
        status: 'planned',
        actualDate: '',
        completionPercentage: 0
      });
    }
  }, [editingPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = {
        classId: classItem?.classId || classItem?._id,
        subjectId: subject?.subjectId || subject?._id,
        labBatchId: labBatch?.batchId || null,
        lessonNumber: parseInt(formData.lessonNumber),
        plannedDate: formData.plannedDate,
        topic: formData.topic,
        subtopics: formData.subtopics ? formData.subtopics.split(',').map(s => s.trim()) : [],
        learningObjectives: formData.learningObjectives ? formData.learningObjectives.split(',').map(s => s.trim()) : [],
        teachingMethodology: formData.teachingMethodology,
        resourcesUsed: formData.resourcesUsed ? formData.resourcesUsed.split(',').map(s => s.trim()) : [],
        additionalNotes: formData.additionalNotes,
        isExtraClass: formData.isExtraClass
      };

      let response;
      if (editingPlan) {
        const updateData = {
          ...data,
          status: formData.status,
          actualDate: formData.actualDate || null,
          completionPercentage: parseInt(formData.completionPercentage)
        };
        response = await api.put(`/api/faculty/lms/lesson-plan/${editingPlan._id}`, updateData);
      } else {
        response = await api.post('/api/faculty/lms/lesson-plan', data);
      }

      if (response.data?.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error saving lesson plan:', error);
      setError(error.response?.data?.message || 'Failed to save lesson plan');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingPlan ? 'Edit Lesson Plan' : 'New Lesson Plan'}
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lesson Number *
                </label>
                <input
                  type="number"
                  value={formData.lessonNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, lessonNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Planned Date *
                </label>
                <input
                  type="date"
                  value={formData.plannedDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, plannedDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topic *
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subtopics (comma separated)
              </label>
              <input
                type="text"
                value={formData.subtopics}
                onChange={(e) => setFormData(prev => ({ ...prev, subtopics: e.target.value }))}
                placeholder="e.g. Variables, Data Types, Operators"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Learning Objectives (comma separated)
              </label>
              <input
                type="text"
                value={formData.learningObjectives}
                onChange={(e) => setFormData(prev => ({ ...prev, learningObjectives: e.target.value }))}
                placeholder="e.g. Understand variables, Learn data types"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teaching Methodology
                </label>
                <select
                  value={formData.teachingMethodology}
                  onChange={(e) => setFormData(prev => ({ ...prev, teachingMethodology: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {methodologies.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resources Used (comma separated)
                </label>
                <input
                  type="text"
                  value={formData.resourcesUsed}
                  onChange={(e) => setFormData(prev => ({ ...prev, resourcesUsed: e.target.value }))}
                  placeholder="e.g. Projector, Whiteboard"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {editingPlan && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="planned">Planned</option>
                    <option value="completed">Completed</option>
                    <option value="rescheduled">Rescheduled</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Actual Date
                  </label>
                  <input
                    type="date"
                    value={formData.actualDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, actualDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isExtraClass"
                checked={formData.isExtraClass}
                onChange={(e) => setFormData(prev => ({ ...prev, isExtraClass: e.target.checked }))}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="isExtraClass" className="text-sm text-gray-700">
                This is an extra class
              </label>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <FiSave className="w-4 h-4" />
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default LessonPlanModal;